import { type ComponentType, type SVGProps, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  MoreVerticalIcon,
  EditIcon,
  RegenerateQuotationIcon,
  GenerateConfirmationIcon,
  GenerateDeliveryOrderIcon,
  GenerateInvoiceIcon,
  ExternalLinkIcon,
} from '../icons';
import { ActivityLogIcon } from '../icons/ActivityLogIcon';
import { useTranslation } from '@hooks/useTranslation';
import { useGenerateOrderDocuments, useGenerateOrderDocument, useCreateXeroInvoice } from '@hooks/orders';
import { useToast } from '@/hooks/use-toast';
import { SUCCESS_TOAST_DURATION } from '../../constants';
import { actionable422Message } from '@/react/api/client';
import { openActivityCentrePanel } from '@components/ActivityCentre/useActivityCentrePanel';
import type { OrderDocumentType, XeroInvoiceMode } from '@api/orders';
import {
  OrderDocumentsGenerateBar,
  type OrderDocumentsGenerateBarMode,
} from './OrderDocumentsGenerateBar';
import { ConfirmationDialog } from '../common/ConfirmationDialog';
import { buildDraftWarning } from './draft-warning';
import { xeroInvoiceUrl } from './xero-invoice-config';
import { spineLinkBlocked, spineLinkBlockedKind, type SpineLinkBlocked } from '@/react/utils/spineLinkError';
import { can, CAPABILITIES, type CurrentUser } from '@/react/lib/capabilities';
import { buildSpineBlockedCopy, type SpineBlockedDocument } from './spine-blocked-copy';
import { useRetainedDialogState } from './useRetainedDialogState';

const ORDER_DOCUMENT_TYPES: readonly OrderDocumentType[] = ['quotation', 'confirmation', 'delivery_order'];

function isOrderDocumentType(key: string): key is OrderDocumentType {
  return (ORDER_DOCUMENT_TYPES as readonly string[]).includes(key);
}

interface OrderAction {
  key: string;
  labelKey: string;
  icon: ComponentType<SVGProps<SVGSVGElement> & { className?: string }>;
}

interface OrderLinkAction extends OrderAction {
  getHref: (orderId: number) => string;
}

const PRIMARY_ACTIONS: OrderLinkAction[] = [
  {
    key: 'edit',
    labelKey: 'order_actions.edit',
    icon: EditIcon,
    getHref: (orderId) => `/orders/${orderId}/edit`,
  },
];

// Each Generate action produces a document for the FULL sales order.
//
// The per-event format selection (a "Full sales order" vs "Per event" submenu)
// was removed in GRAIN-2077. Re-adding per-event generation is tracked under the
// "[out of scope] Generate PDFs on a per-event level" milestone:
//   GRAIN-1517 (quotation), GRAIN-1516 (confirmation), GRAIN-1515 (delivery order).
// To restore the old UI, revert/cherry-pick the GRAIN-2077 removal commit.
const GENERATE_ACTIONS: OrderAction[] = [
  {
    key: 'quotation',
    labelKey: 'order_actions.quotation',
    icon: RegenerateQuotationIcon,
  },
  {
    key: 'confirmation',
    labelKey: 'order_actions.confirmation',
    icon: GenerateConfirmationIcon,
  },
  {
    key: 'delivery_order',
    labelKey: 'order_actions.delivery_order',
    icon: GenerateDeliveryOrderIcon,
  },
];

// Consistent icon size for dropdown menu items (14x14px)
const DROPDOWN_ICON_SIZE = 14;
const dropdownIconStyle = { width: DROPDOWN_ICON_SIZE, height: DROPDOWN_ICON_SIZE };

// Static class string — computed once at module load, not per render.
const itemClassName = cn(
  'relative isolate flex items-center gap-2 px-6 py-2 w-full',
  'font-inter text-sm font-semibold leading-[1.25] cursor-pointer text-off-black',
  'hover:bg-transparent focus:bg-transparent data-[highlighted]:bg-transparent',
  'before:absolute before:inset-y-0 before:left-4 before:right-4 before:-z-10 before:rounded-xs',
  'hover:before:bg-dinnerplate-white-light focus:before:bg-dinnerplate-white-light',
  'data-[highlighted]:before:bg-dinnerplate-white-light'
);

interface OrderActionsDropdownProps {
  orderId: number;
  /** Draft events (event_id) omitted from documents — warned before generating. */
  draftEvents: string[];
  /** False when the order has no event that would render — disables generation. */
  hasPublishableEvents: boolean;
  /** Order status (lead/confirmed/cancelled) — generating/regenerating a Xero invoice is confirmed-only. */
  orderStatus: string;
  /** Set once an invoice exists — swaps "Generate" for "View" + "Regenerate". */
  xeroInvoiceId: string | null;
  /** True when the order is small enough to render on the spot in a new tab. */
  canGenerateDocumentsDirectly: boolean;
  /** Signed-in user — gates the menu actions (History reads, Edit/Generate write). */
  currentUser?: CurrentUser;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
}

export function OrderActionsDropdown({
  orderId,
  draftEvents,
  hasPublishableEvents,
  orderStatus,
  xeroInvoiceId,
  canGenerateDocumentsDirectly,
  currentUser,
  side,
  align = 'end',
  sideOffset,
}: OrderActionsDropdownProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const generateDocuments = useGenerateOrderDocuments();
  const generateDocument = useGenerateOrderDocument();
  const createXeroInvoice = useCreateXeroInvoice();
  // The document type awaiting draft-exclusion confirmation; null when closed.
  const [pendingDocument, setPendingDocument] = useState<OrderDocumentType | null>(null);
  // Server-refused generation because a linked Spine order is cancelled/deleted
  // in Spine: an info-only dialog with copy for every reported kind,
  // instead of the generic error bar/toast.
  const spineBlockedDialog = useRetainedDialogState<{
    blocked: SpineLinkBlocked;
    documentType: SpineBlockedDocument;
  }>();

  // Small orders (GRAIN-2469) render on the spot and open the PDF in a new tab;
  // the tab is opened synchronously — before the async mutation — so the browser
  // doesn't treat it as a popup, falling back to the current tab if blocked.
  //
  // Larger orders stay async (GRAIN-2326): the document lands in the Activity
  // Centre and the mutation lifecycle drives the floating generate bar
  // (loading → queued → error), same feedback pattern as the events home dock.
  const fireGenerate = (documentType: OrderDocumentType) => {
    if (canGenerateDocumentsDirectly) {
      const newTab = window.open('', '_blank');
      generateDocument.mutate(
        { orderId, documentType },
        {
          onSuccess: ({ url }) => {
            if (newTab) {
              newTab.location.href = url;
            } else {
              window.location.href = url;
            }
            generateDocument.reset();
          },
          onError: (error) => {
            newTab?.close();
            handleSpineBlocked(error, generateDocument.reset, documentType);
          },
        }
      );
      return;
    }

    if (generateDocuments.isPending) return; // guard against a double-fire before disabled re-renders
    generateDocuments.mutate(
      { orderId, documentTypes: [documentType] },
      { onError: (error) => handleSpineBlocked(error, generateDocuments.reset, documentType) }
    );
  };

  // Reset typed Spine-link failures so the generic error state stays hidden.
  const handleSpineBlocked = (error: unknown, reset: () => void, documentType: SpineBlockedDocument) => {
    const blocked = spineLinkBlocked(error);
    if (!blocked) return;
    spineBlockedDialog.show({ blocked, documentType });
    reset();
  };

  // Generating/regenerating a Xero invoice is a real financial write — only a
  // confirmed order can raise one. Viewing an existing invoice is read-only and
  // always allowed once one exists.
  const canGenerateInvoice = orderStatus === 'confirmed';
  const hasXeroInvoice = Boolean(xeroInvoiceId);

  // Capability gates (server policy stays authoritative — this only hides
  // actions). History is a read; Edit and every Generate action are writes.
  // Viewing an already-minted Xero invoice is read-only.
  const canReadOrders = can(currentUser, CAPABILITIES.ORDERS_READ);
  const canWriteOrders = can(currentUser, CAPABILITIES.ORDERS_WRITE);
  // The Generate group shows if the user can produce documents (write), or can
  // at least view an existing invoice (read + invoice present).
  const showGenerateSection = canWriteOrders || (canReadOrders && hasXeroInvoice);

  const isSalesOrderCancelled = orderStatus === 'cancelled';
  const canGenerateSalesDocuments = hasPublishableEvents && !isSalesOrderCancelled;
  const cancelledReason = isSalesOrderCancelled ? t('order_actions.cancelled_reason') : undefined;

  const openXeroInvoice = () => {
    if (!xeroInvoiceId) return;
    window.open(xeroInvoiceUrl(xeroInvoiceId), '_blank', 'noopener,noreferrer');
  };

  // Generate runs synchronously (mirrors Papercut): a loading toast covers the
  // Papercut→Xero round-trip, then success/error. The order query is invalidated
  // by the hook, so the menu re-renders with View + Regenerate.
  //
  // On success we open the invoice in Xero so the user lands on it directly,
  // rather than having to find the "View" option that appears afterward. The tab
  // is opened synchronously — before the async mutation — so the browser doesn't
  // treat it as a popup, falling back to the current tab if blocked (matches
  // fireGenerate's document-view pattern).
  const fireXeroInvoice = () => {
    if (!canGenerateInvoice) return; // guarded here too — aria-disabled doesn't block a DOM click
    // Block overlapping fires: the menu item is disabled by order status, not by
    // in-flight state, so reopening the menu mid-request would open a second tab
    // and start a second mutation whose callbacks orphan the first tab.
    if (createXeroInvoice.isPending) return;
    // Derive the mode from state, not the button: an order that already holds an
    // invoice regenerates — safer than minting a second one if the button lags.
    const mode: XeroInvoiceMode = hasXeroInvoice ? 'regenerate' : 'generate';
    const newTab = window.open('', '_blank');
    const pending = toast({ description: t('order_actions.xero_invoice_generating'), duration: Infinity });
    createXeroInvoice.mutate(
      { orderId, mode },
      {
        onSuccess: ({ xero_invoice_url, xero_contact_replaced }) => {
          pending.dismiss();
          if (newTab) {
            newTab.location.href = xero_invoice_url;
          } else {
            window.location.href = xero_invoice_url;
          }
          const done = t(
            mode === 'regenerate'
              ? 'order_actions.xero_invoice_regenerated'
              : 'order_actions.xero_invoice_generated'
          );
          toast({
            description: xero_contact_replaced ? `${done} ${t('order_actions.xero_contact_replaced')}` : done,
            duration: SUCCESS_TOAST_DURATION,
          });
        },
        onError: (error) => {
          newTab?.close();
          pending.dismiss();
          if (spineLinkBlockedKind(error)) {
            handleSpineBlocked(error, createXeroInvoice.reset, 'xero_invoice');
            return;
          }
          toast({
            description: actionable422Message(error) ?? t('order_actions.xero_invoice_error'),
            variant: 'destructive',
          });
        },
      }
    );
  };

  const handleGenerateClick = (actionKey: string) => {
    if (!isOrderDocumentType(actionKey)) return;
    if (!canGenerateSalesDocuments) return; // guarded here too — aria-disabled doesn't block a DOM click

    // Warn which draft events will be excluded before generating a mixed order's
    // document; with no drafts, generate immediately.
    if (draftEvents.length > 0) {
      setPendingDocument(actionKey);
      return;
    }
    fireGenerate(actionKey);
  };

  // Whichever async document generation is mid-flight drives the shared feedback
  // bar. The Xero invoice runs synchronously (toast-driven), so it's not here.
  const generateMutations = [generateDocument, generateDocuments];
  const activeMutation =
    generateMutations.find((m) => m.isPending || m.isSuccess || m.isError) ?? generateDocuments;

  const handleConfirmGenerate = () => {
    if (pendingDocument) fireGenerate(pendingDocument);
    setPendingDocument(null);
  };

  const draftWarning = pendingDocument
    ? buildDraftWarning(draftEvents, pendingDocument, t)
    : null;

  const spineBlockedCopy = spineBlockedDialog.value
    ? buildSpineBlockedCopy(
        spineBlockedDialog.value.blocked,
        spineBlockedDialog.value.documentType,
        t,
      )
    : { title: '', message: '' };

  const generateBarMode: OrderDocumentsGenerateBarMode | null =
    activeMutation.isPending ? 'loading'
    : activeMutation.isSuccess ? 'queued'
    : activeMutation.isError ? 'error'
    : null;

  const resetGenerateBar = () => {
    generateDocuments.reset();
    generateDocument.reset();
  };

  return (
    <>
      <OrderDocumentsGenerateBar
        mode={generateBarMode}
        onDismiss={resetGenerateBar}
        onAutoDismiss={resetGenerateBar}
        onQueuedLinkClick={() => openActivityCentrePanel('processing')}
      />
      <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost-menu"
          size="icon-sm"
          aria-label={t('order_actions.trigger_label')}
        >
          <MoreVerticalIcon className="h-4 w-1 text-grey group-hover:text-off-black group-data-[state=open]:text-off-black" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        side={side}
        align={align}
        sideOffset={sideOffset}
        className={cn(
          'w-[232px] px-0 py-4 rounded-lg border-[0.5px]',
          'border-dinnerplate-white bg-off-white',
          'shadow-soft flex flex-col gap-4'
        )}
      >
        {/* Primary actions: Edit (write), History (read) */}
        <div className="flex flex-col gap-1">
          {canWriteOrders && PRIMARY_ACTIONS.map((action) => {
            const Icon = action.icon;
            return (
              <DropdownMenuItem key={action.key} asChild className={itemClassName}>
                <a href={action.getHref(orderId)}>
                  <Icon className="shrink-0" style={dropdownIconStyle} />
                  {t(action.labelKey)}
                </a>
              </DropdownMenuItem>
            );
          })}

          {canReadOrders && (
            <DropdownMenuItem asChild className={itemClassName}>
              <Link to="/orders/$id/history" params={{ id: String(orderId) }}>
                <ActivityLogIcon className="shrink-0" style={dropdownIconStyle} />
                {t('order_actions.history')}
              </Link>
            </DropdownMenuItem>
          )}
        </div>

        {showGenerateSection && (
        <>
        <DropdownMenuSeparator className="bg-dinnerplate-white h-px mx-4 my-0" />

        {/* Generate section: each item generates for the full sales order */}
        <div className="flex flex-col gap-2 w-full">
          <span className="px-6 font-inter text-sm font-semibold leading-[1.25] text-grey">
            {t('order_actions.generate_section')}
          </span>
          <div className="flex flex-col gap-1">
            <TooltipProvider delayDuration={150}>
              {canWriteOrders && GENERATE_ACTIONS.map((action) => {
                const Icon = action.icon;
                const item = (
                  <DropdownMenuItem
                    key={action.key}
                    onClick={() => handleGenerateClick(action.key)}
                    disabled={!canGenerateSalesDocuments}
                    className={cn(itemClassName, cancelledReason && 'data-[disabled]:!pointer-events-auto')}
                  >
                    <Icon className="shrink-0" style={dropdownIconStyle} />
                    {t(action.labelKey)}
                  </DropdownMenuItem>
                );

                if (!cancelledReason) return item;

                return (
                  <Tooltip key={action.key}>
                    <TooltipTrigger asChild>{item}</TooltipTrigger>
                    <TooltipContent variant="dark" side="right" className="max-w-none whitespace-nowrap">
                      {cancelledReason}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </TooltipProvider>

            {/* Xero invoice: viewing an existing one is read-only; generating or
                regenerating is a write. Generate/regenerate are confirmed-only. */}
            {hasXeroInvoice && canReadOrders && (
              <DropdownMenuItem
                key="view_xero_invoice"
                onClick={openXeroInvoice}
                className={itemClassName}
              >
                <ExternalLinkIcon className="shrink-0 h-3.5 w-3.5" />
                {t('order_actions.view_xero_invoice')}
              </DropdownMenuItem>
            )}
            {hasXeroInvoice && canWriteOrders && (
              <DropdownMenuItem
                key="regenerate_xero_invoice"
                onClick={fireXeroInvoice}
                disabled={!canGenerateInvoice}
                className={itemClassName}
              >
                <GenerateInvoiceIcon className="shrink-0" style={dropdownIconStyle} />
                {t('order_actions.regenerate_xero_invoice')}
              </DropdownMenuItem>
            )}
            {!hasXeroInvoice && canWriteOrders && (
              <DropdownMenuItem
                key="xero_invoice"
                onClick={fireXeroInvoice}
                disabled={!canGenerateInvoice}
                className={itemClassName}
              >
                <GenerateInvoiceIcon className="shrink-0" style={dropdownIconStyle} />
                {t('order_actions.generate_xero_invoice')}
              </DropdownMenuItem>
            )}
          </div>
        </div>
        </>
        )}
      </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmationDialog
        open={pendingDocument !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDocument(null);
        }}
        onConfirm={handleConfirmGenerate}
        // Chilli-red per design (Figma) — attention colour, not an error state.
        variant="destructive"
        title={draftWarning?.title ?? ''}
        message={draftWarning?.message ?? ''}
        confirmLabel={t('order_actions.generate_draft_dialog.confirm')}
        cancelLabel={t('order_actions.generate_draft_dialog.cancel')}
      />
      <ConfirmationDialog
        open={spineBlockedDialog.open}
        onOpenChange={spineBlockedDialog.setOpen}
        onConfirm={() => spineBlockedDialog.setOpen(false)}
        title={spineBlockedCopy.title}
        message={spineBlockedCopy.message}
        hideActions
      />
    </>
  );
}
